import {
  buildMfaTwoFactorFromSession,
  clearLoginTimestamp,
  createInitialTwoFactorState,
  getVenueSessionDuration,
  isRememberMe,
  isSessionExpired,
  logAuditEvent,
} from "@dwelldrop/shared";
import { getSupabase, type Session, type User } from "@dwelldrop/supabase";
import type { Dispatch, SetStateAction } from "react";
import { auditActionSource } from "../utils/auditActionSource";
import { VENUE_TOKEN_KEY } from "./venueMfaConfig";

export { VENUE_TOKEN_KEY };
export const VENUE_STORAGE_PREFIX = "venuehub_venue" as const;

export const RATE_LIMIT_WINDOW = 15 * 60 * 1000;
export const MAX_SMS_SENDS = 3;
export const MAX_FAILED_ATTEMPTS = 5;
export const DEFAULT_LOCKOUT_DURATION = 15 * 60 * 1000;
export const SMS_CODE_LENGTH = 6;
export const SUSPENDED_ERROR_MESSAGE = "Account suspended. Contact support.";

export type VenueProfile = {
  id: string;
  role: "venue_owner" | "venue_staff" | "property_operator";
  display_name: string | null;
  display_name_raw: string | null;
  phone: string | null;
  staffRole: "scanner" | "manager" | null;
};

export type TwoFactorState = {
  enabled: boolean;
  pendingVerification: boolean;
  maskedPhone?: string | null;
  smsError?: string | null;
  mfaFactorId: string | null;
  mfaChallengeId: string | null;
};

export type RateLimitState = {
  smsSendCount?: number;
  smsSendWindowStart?: number;
  failedAttempts: number;
  lockedUntil: number | null;
};

export type AuthState = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: VenueProfile | null;
  twoFactor: TwoFactorState;
  sessionExpired: boolean;
};

export type PendingSession = {
  user: User;
  session: Session;
  profile: VenueProfile;
  rememberMe: boolean;
};

export { auditActionSource } from "../utils/auditActionSource";

export function toE164(phone: string | null): string | null {
  return phone ? `+${phone}` : null;
}

export function maskPhone(phone: string): string {
  if (phone.length <= 6) return phone;
  return phone.slice(0, 3) + "****" + phone.slice(-4);
}

export async function fetchProfile(
  userId: string,
  retries = 2,
  delayMs = 1000,
  accessToken?: string
): Promise<VenueProfile | null> {
  const supabase = getSupabase(VENUE_TOKEN_KEY);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  for (let attempt = 0; attempt <= retries; attempt++) {
    let data: Omit<VenueProfile, "staffRole"> | null = null;
    let status = 0;

    try {
      if (accessToken) {
        const res = await Promise.race([
          fetch(
            `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&role=in.(venue_owner,venue_staff,property_operator)&select=id,role,display_name,display_name_raw,phone&limit=1`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                apikey: anonKey,
                Accept: "application/json",
              },
            }
          ),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("fetchProfile timeout after 8s")),
              8000
            )
          ),
        ]);
        status = res.status;
        if (res.ok) {
          const rows: Omit<VenueProfile, "staffRole">[] = await res.json();
          data = rows[0] ?? null;
        }
      } else {
        const result = await Promise.race([
          supabase
            .from("profiles")
            .select(
              "id, role, display_name, display_name_raw, phone"
            )
            .eq("id", userId)
            .in("role", ["venue_owner", "venue_staff", "property_operator"])
            .single(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("fetchProfile timeout after 8s")),
              8000
            )
          ),
        ]);
        data = result.data;
        status = result.status ?? 0;
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[VenueAuth] fetchProfile failed:", msg);
      status = 0;
    }

    if (data) {
      let staffRole: "scanner" | "manager" | null = null;
      if (data.role === "venue_staff") {
        if (accessToken) {
          const res = await fetch(
            `${supabaseUrl}/rest/v1/venue_staff?user_id=eq.${userId}&select=role&limit=1`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                apikey: anonKey,
                Accept: "application/json",
              },
            }
          );
          if (res.ok) {
            const rows: { role: string }[] = await res.json();
            const role = rows[0]?.role;
            if (role === "scanner" || role === "manager") staffRole = role;
          }
        } else {
          const { data: staffData } = await supabase
            .from("venue_staff")
            .select("role")
            .eq("user_id", userId)
            .single();
          if (staffData?.role === "scanner" || staffData?.role === "manager") {
            staffRole = staffData.role;
          }
        }
      }
      return { ...data, staffRole };
    }

    const isTransient = status === 0 || status === undefined;
    if (!isTransient) {
      return null;
    }
    if (attempt < retries) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
}

export async function checkVenueAccessForLogin(
  role: VenueProfile["role"],
  userId: string,
  accessToken: string
): Promise<boolean> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const headers = {
    apikey: anonKey,
    Accept: "application/json",
    Authorization: `Bearer ${accessToken}`,
  };

  try {
    if (role === "venue_staff") {
      const staffRes = await fetch(
        `${supabaseUrl}/rest/v1/venue_staff?user_id=eq.${userId}&status=eq.active&select=venue_id`,
        { headers }
      );
      if (!staffRes.ok) return true;
      const staffRows: { venue_id: string }[] = await staffRes.json();
      if (staffRows.length === 0) return false;

      const venueIds = staffRows.map((r) => r.venue_id).join(",");
      const venueRes = await fetch(
        `${supabaseUrl}/rest/v1/venues?id=in.(${venueIds})&status=in.(active,pending)&deleted_at=is.null&select=id&limit=1`,
        { headers }
      );
      if (!venueRes.ok) return true;
      const venueRows: { id: string }[] = await venueRes.json();
      return venueRows.length > 0;
    }

    if (role === "venue_owner") {
      const usableRes = await fetch(
        `${supabaseUrl}/rest/v1/venues?owner_id=eq.${userId}&status=in.(active,pending)&deleted_at=is.null&select=id&limit=1`,
        { headers }
      );
      if (!usableRes.ok) return true;
      const usableRows: { id: string }[] = await usableRes.json();
      if (usableRows.length > 0) return true;

      const anyRes = await fetch(
        `${supabaseUrl}/rest/v1/venues?owner_id=eq.${userId}&select=id&limit=1`,
        { headers }
      );
      if (!anyRes.ok) return true;
      const anyRows: { id: string }[] = await anyRes.json();
      return anyRows.length === 0;
    }

    return true;
  } catch {
    return true;
  }
}

export function getLockoutMessage(lockedUntil: number | null): string {
  const remainingSec = lockedUntil
    ? Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000))
    : 900;
  const remainingMin = Math.max(1, Math.ceil(remainingSec / 60));
  return `Account temporarily locked. Try again in ${remainingMin} minute${remainingMin > 1 ? "s" : ""}.`;
}

export function checkSMSRateLimit(
  rateLimit: RateLimitState,
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>
): string | null {
  const now = Date.now();
  const windowStart = rateLimit.smsSendWindowStart ?? 0;
  const count = rateLimit.smsSendCount ?? 0;
  if (now - windowStart > RATE_LIMIT_WINDOW) {
    setRateLimit((r) => ({
      ...r,
      smsSendCount: 0,
      smsSendWindowStart: now,
    }));
    return null;
  }
  if (count >= MAX_SMS_SENDS) {
    const remainingMs = RATE_LIMIT_WINDOW - (now - windowStart);
    const remainingMin = Math.ceil(remainingMs / 60000);
    return `Too many SMS requests. Please try again in ${remainingMin} minute${remainingMin > 1 ? "s" : ""}.`;
  }
  return null;
}

export function recordSMSSend(
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>
): void {
  const now = Date.now();
  setRateLimit((r) => {
    const windowStart = r.smsSendWindowStart ?? 0;
    const count = r.smsSendCount ?? 0;
    const inWindow = now - windowStart < RATE_LIMIT_WINDOW;
    return {
      ...r,
      smsSendCount: inWindow ? count + 1 : 1,
      smsSendWindowStart: inWindow ? windowStart : now,
    };
  });
}

export function recordFailedAttempt(
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>,
  lockoutMs?: number
): boolean {
  let triggered = false;
  setRateLimit((r) => {
    const newFailed = r.failedAttempts + 1;
    if (newFailed >= MAX_FAILED_ATTEMPTS) {
      triggered = true;
      return {
        ...r,
        failedAttempts: newFailed,
        lockedUntil: Date.now() + (lockoutMs ?? DEFAULT_LOCKOUT_DURATION),
      };
    }
    return { ...r, failedAttempts: newFailed };
  });
  return triggered;
}

export function clearFailedAttempts(
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>
): void {
  setRateLimit((r) => ({ ...r, failedAttempts: 0, lockedUntil: null }));
}

export function computeIsLockedOut(
  lockedUntil: number | null,
  setRateLimit: Dispatch<SetStateAction<RateLimitState>>
): boolean {
  if (!lockedUntil) return false;
  if (Date.now() > lockedUntil) {
    setRateLimit((r) => ({
      ...r,
      failedAttempts: 0,
      lockedUntil: null,
    }));
    return false;
  }
  return true;
}

export function clearVenueAuthStorage(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split("; ").forEach((c) => {
      const name = c.split("=")[0];
      if (name.startsWith("venuehub_") && name.includes("token")) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Strict`;
      }
    });
  } catch {
    // Browser storage might be disabled
  }
}

export function clearSelectedVenueForUser(userId: string): void {
  try {
    localStorage.removeItem(`dwelldrop_selected_venue:${userId}`);
  } catch {
    // storage disabled
  }
}

export async function expireSessionAndRedirect(
  profile: VenueProfile,
  userId: string | null
): Promise<void> {
  const supabase = getSupabase(VENUE_TOKEN_KEY);
  await logAuditEvent(
    supabase,
    "session_expired",
    userId,
    {
      app: "venue",
      role: profile.role,
    },
    "auth",
    auditActionSource()
  );
  clearLoginTimestamp(VENUE_STORAGE_PREFIX);
  await supabase.auth.signOut();
  window.location.href = "/login?expired=true";
}

export async function handleVisibilitySessionCheck(
  profile: VenueProfile | null
): Promise<boolean> {
  if (!profile) return false;
  const rememberMe = isRememberMe(VENUE_STORAGE_PREFIX);
  const maxDuration = getVenueSessionDuration(profile.role, rememberMe);
  if (isSessionExpired(VENUE_STORAGE_PREFIX, maxDuration)) {
    clearLoginTimestamp(VENUE_STORAGE_PREFIX);
    getSupabase(VENUE_TOKEN_KEY).auth.signOut();
    window.location.href = "/login?expired=true";
    return true;
  }
  return false;
}

export async function handleNavigationSessionCheck(
  profile: VenueProfile | null
): Promise<void> {
  if (!profile) return;
  const rememberMe = isRememberMe(VENUE_STORAGE_PREFIX);
  const maxDuration = getVenueSessionDuration(profile.role, rememberMe);
  if (isSessionExpired(VENUE_STORAGE_PREFIX, maxDuration)) {
    clearLoginTimestamp(VENUE_STORAGE_PREFIX);
    getSupabase(VENUE_TOKEN_KEY).auth.signOut();
  }
}

const emptyTwoFactor: TwoFactorState = createInitialTwoFactorState();

export function buildSignedOutState(): AuthState {
  return {
    user: null,
    session: null,
    loading: false,
    profile: null,
    sessionExpired: false,
    twoFactor: emptyTwoFactor,
  };
}

export async function signOutVenueUser(
  userId: string | null,
  isSigningOutRef: { current: boolean },
  setState: Dispatch<SetStateAction<AuthState>>,
  setPendingSession: Dispatch<SetStateAction<PendingSession | null>>
): Promise<void> {
  const supabase = getSupabase(VENUE_TOKEN_KEY);
  await logAuditEvent(
    supabase,
    "logout",
    userId,
    { app: "venue" },
    "auth",
    auditActionSource()
  );
  isSigningOutRef.current = true;
  try {
    await supabase.auth.signOut();
  } finally {
    isSigningOutRef.current = false;
  }
  clearLoginTimestamp(VENUE_STORAGE_PREFIX);
  clearVenueAuthStorage();
  setState(buildSignedOutState());
  setPendingSession(null);
}

export async function signOutAllVenueDevices(
  userId: string | null,
  isSigningOutRef: { current: boolean },
  broadcastForceLogout: (reason: string) => Promise<void>,
  setState: Dispatch<SetStateAction<AuthState>>,
  setPendingSession: Dispatch<SetStateAction<PendingSession | null>>
): Promise<void> {
  const supabase = getSupabase(VENUE_TOKEN_KEY);
  await logAuditEvent(
    supabase,
    "logout_all_devices",
    userId,
    { app: "venue" },
    "auth",
    auditActionSource()
  );
  await broadcastForceLogout("sign_out_all_devices");
  isSigningOutRef.current = true;
  try {
    await supabase.auth.signOut({ scope: "global" });
  } finally {
    isSigningOutRef.current = false;
  }
  clearVenueAuthStorage();
  setState(buildSignedOutState());
  setPendingSession(null);
}

export type AuthStateChangeDeps = {
  setState: Dispatch<SetStateAction<AuthState>>;
  profileRef: { current: VenueProfile | null };
  fetchInitiated: { current: boolean };
  signInInProgress: { current: boolean };
  isSigningOutRef: { current: boolean };
  /** True while login is waiting on SMS MFA (avoids auth lock deadlock with mfa.verify). */
  pendingMfaVerificationRef?: { current: boolean };
  /** True while mfaVerifyLoginChallenge is finishing session setup. */
  mfaCompletingRef?: { current: boolean };
  setPendingSession?: Dispatch<SetStateAction<PendingSession | null>>;
  rateLimit?: RateLimitState;
  setRateLimit?: Dispatch<SetStateAction<RateLimitState>>;
};

async function resolveVenueTwoFactorForSession(
  profile: VenueProfile
): Promise<TwoFactorState | "abandoned"> {
  const bits = await buildMfaTwoFactorFromSession(
    VENUE_TOKEN_KEY,
    profile.phone
  );

  // A restored AAL1 session that still needs an MFA challenge means the user
  // closed/refreshed mid-verification. Sign out and force full re-login rather
  // than silently re-issuing an SMS challenge.
  if (bits.pendingVerification) {
    await getSupabase(VENUE_TOKEN_KEY).auth.signOut();
    return "abandoned";
  }

  return {
    ...createInitialTwoFactorState(),
    ...bits,
  };
}

export async function processAuthStateChange(
  event: string,
  session: Session | null,
  deps: AuthStateChangeDeps
): Promise<void> {
  const {
    setState,
    profileRef,
    fetchInitiated,
    signInInProgress,
    isSigningOutRef,
    pendingMfaVerificationRef,
    mfaCompletingRef,
  } = deps;

  // MFA verify holds the auth lock until listeners finish; do not call auth.mfa.* here.
  if (event === "MFA_CHALLENGE_VERIFIED") {
    return;
  }

  if (pendingMfaVerificationRef?.current || mfaCompletingRef?.current) {
    return;
  }

  if (event === "TOKEN_REFRESHED") {
    const currentProfile = profileRef.current;
    if (currentProfile) {
      const rememberMe = isRememberMe(VENUE_STORAGE_PREFIX);
      const maxDuration = getVenueSessionDuration(
        currentProfile.role,
        rememberMe
      );
      if (isSessionExpired(VENUE_STORAGE_PREFIX, maxDuration)) {
        await expireSessionAndRedirect(
          currentProfile,
          session?.user?.id ?? null
        );
        return;
      }
    }
    setState((s) => ({ ...s, session, loading: false }));
    return;
  }

  if (signInInProgress.current) {
    return;
  }

  if (event === "SIGNED_OUT" || !session?.user) {
    fetchInitiated.current = false;
    setState((s) => ({
      ...s,
      user: session?.user ?? null,
      session,
      loading: false,
      profile: null,
      sessionExpired: !!profileRef.current && !isSigningOutRef.current,
    }));
    return;
  }

  if (profileRef.current) {
    if (profileRef.current.id !== session.user.id) {
      clearSelectedVenueForUser(profileRef.current.id);
      clearLoginTimestamp(VENUE_STORAGE_PREFIX);
      fetchInitiated.current = false;
      setState((s) => ({
        ...s,
        user: session.user,
        session,
        profile: null,
      }));
    } else {
      setState((s) => ({
        ...s,
        user: s.user?.id === session.user.id ? s.user : session.user,
        session,
        loading: false,
      }));
      return;
    }
  }

  if (fetchInitiated.current) {
    setState((s) => ({
      ...s,
      user: session.user,
      session,
      loading: false,
    }));
    return;
  }

  fetchInitiated.current = true;
  const profile = await fetchProfile(
    session.user.id,
    2,
    1000,
    session.access_token
  );

  if (profile) {
    const twoFactor = await resolveVenueTwoFactorForSession(profile);
    if (twoFactor === "abandoned") {
      fetchInitiated.current = false;
      setState(buildSignedOutState());
      deps.setPendingSession?.(null);
      return;
    }
    setState((s) => {
      if (s.profile)
        return { ...s, user: session.user, session, loading: false };
      return {
        ...s,
        user: session.user,
        session,
        loading: false,
        profile,
        twoFactor,
      };
    });
    return;
  }

  setState((s) => {
    if (s.profile) {
      return { ...s, user: session.user, session, loading: false };
    }
    return { ...s, user: session.user, session, loading: false };
  });

  const bgUserId = session.user.id;
  setTimeout(async () => {
    if (profileRef.current) return;
    const retryProfile = await fetchProfile(
      bgUserId,
      2,
      1500,
      session.access_token
    );
    if (retryProfile) {
      const twoFactor = await resolveVenueTwoFactorForSession(retryProfile);
      if (twoFactor === "abandoned") {
        fetchInitiated.current = false;
        setState(buildSignedOutState());
        deps.setPendingSession?.(null);
        return;
      }
      setState((s) => {
        if (s.profile) return s;
        return {
          ...s,
          loading: false,
          profile: retryProfile,
          twoFactor,
        };
      });
    } else {
      setState((s) =>
        s.profile ? s : { ...s, loading: false, profile: null }
      );
    }
  }, 2000);
}
